/**
 * Confirmation dialog for a unified refresh plan (recalculation + snapshot refresh).
 *
 * Shows the planned steps with resource counts, expandable to view individual IDs.
 * Auto-confirms after AUTOCONFIRM_MS unless the user interacts with the dialog.
 */
import React, { useCallback, useEffect, useRef, useState, type FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faPlay } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Button } from "@/components/ui/button/Button";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "@/components/ui/dialog/Dialog";
import type { RefreshPlanStep } from "@/services/api/data-hub/types";

const AUTOCONFIRM_MS = 15000;
const TICK_MS = 1000;

interface RefreshPreviewDialogProps {
  open: boolean;
  title: string;
  steps: RefreshPlanStep[];
  totalResources: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const RefreshPreviewDialog: FC<RefreshPreviewDialogProps> = ({ open, title, steps, totalResources, onConfirm, onCancel }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const confirmedRef = useRef(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (!open) return;
    setExpandedIdx(null);
    setElapsed(0);
    setPaused(false);
    confirmedRef.current = false;
  }, [open]);

  // Countdown timer — auto-confirms when elapsed reaches AUTOCONFIRM_MS
  useEffect(() => {
    if (!open || confirmedRef.current || paused) return;

    const interval = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + TICK_MS;
        if (next >= AUTOCONFIRM_MS && !confirmedRef.current) {
          confirmedRef.current = true;
          onConfirm();
        }
        return next;
      });
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [open, paused, onConfirm]);

  // Any click inside the dialog body pauses the timer
  const handleInteraction = useCallback(() => {
    if (!paused) setPaused(true);
  }, [paused]);

  const handleConfirm = useCallback(() => {
    if (confirmedRef.current) return;
    confirmedRef.current = true;
    onConfirm();
  }, [onConfirm]);

  const toggleStep = useCallback((idx: number) => {
    setExpandedIdx((prev) => (prev === idx ? null : idx));
  }, []);

  const remainingSeconds = Math.max(0, Math.ceil((AUTOCONFIRM_MS - elapsed) / 1000));

  return (
    <Dialog open={open} onClose={onCancel} size="3xl">
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div onClick={handleInteraction}>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          {totalResources} resource{totalResources !== 1 ? "s" : ""} will be processed in {steps.length} step{steps.length !== 1 ? "s" : ""}.
        </p>

        <ul className="space-y-1" data-testid="refresh-plan-steps">
          {steps.map((step, idx) => {
            const isExpanded = expandedIdx === idx;

            return (
              <li key={idx}>
                <button
                  type="button"
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                  onClick={() => toggleStep(idx)}
                  data-testid={`refresh-step-${idx}`}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    className="w-3 text-slate-400 dark:text-slate-500"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 flex-1">
                    {step.label}
                  </span>
                  <span className="text-xs tabular-nums text-slate-400 dark:text-slate-500">
                    ({step.count})
                  </span>
                </button>

                {isExpanded && step.resource_ids.length > 0 && (
                  <ul className="ml-8 mt-1 mb-2 space-y-0.5 max-h-40 overflow-y-auto scrollbar-hide">
                    {step.resource_ids.map((id) => (
                      <li key={id} className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate px-2 py-0.5">
                        {id}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        </div>
      </DialogBody>
      <DialogActions>
        {!paused && (
          <p className="mr-auto text-xs text-slate-400 dark:text-slate-500">
            Auto-confirming in {remainingSeconds}s &mdash; click the dialog to pause
          </p>
        )}
        <Button plain onClick={onCancel} data-testid="refresh-preview-cancel">
          Skip
        </Button>
        <Button color="indigo" onClick={handleConfirm} data-testid="refresh-preview-confirm">
          <FontAwesomeIcon icon={faPlay} className="mr-1.5" />
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefreshPreviewDialog;
